import classNames from 'classnames'
import React from 'react'
import { useTranslation } from 'react-i18next'

import Breakpoints from '@/constants/Breakpoints'

import useWindowSize from '@/hooks/useWindowSize'

import FreeGift from 'assets/images/illustrations/free-gift-present.svg'
import GenericImage from 'assets/images/illustrations/product-thumb-generic-free-gift.svg'

import CloudinaryImage from '@/components/elements/atoms/CloudinaryImage'
import Text from '@/components/elements/atoms/Text'

// Styles
import STYLES from './ProductThumb.module.sass'

type Background = 'brand-yellow-300' | 'brand-yellow-200' | 'transparent'

type Props = {
  image?: string
  alt: string
  quantity?: number | null
  imageSize?: { mobile: number; desktop: number }
  background?: Background
  hasBorderRadius?: boolean
  requiresPayment?: boolean
  showLabel?: boolean
}

const FootBar = ({
  isFree,
  quantity
}: {
  isFree?: boolean
  quantity?: number | null
}) => {
  const { t } = useTranslation()

  if (isFree) {
    return (
      <div className={STYLES.quantity}>
        <img
          src={FreeGift}
          alt={t('molecules.product_thumb.free_gift')}
          className={STYLES.freeGift}
        />
        <Text
          text="product_thumb.free_gift"
          namespace="molecules"
          element="span"
        />
      </div>
    )
  }

  if (quantity) {
    return (
      <div className={STYLES.quantity}>
        <Text
          text="product_thumb.quantity"
          namespace="molecules"
          element="span"
          variables={{ quantity }}
        />
      </div>
    )
  }

  return null
}

const Image = ({
  image,
  imageSize,
  alt
}: {
  image: string | undefined
  imageSize: { mobile: number; desktop: number }
  alt: string
}) => {
  const { windowWidth } = useWindowSize()
  const isMobile = windowWidth < Breakpoints.md

  const { t } = useTranslation()

  if (image) {
    return (
      <CloudinaryImage
        className={STYLES.productImage}
        image={{
          path: image,
          crop: 'fill',
          width: isMobile ? imageSize.mobile : imageSize.desktop,
          height: isMobile ? imageSize.mobile : imageSize.desktop,
          dpr: window.devicePixelRatio
        }}
        alt={alt}
      />
    )
  }

  return (
    <img
      className={STYLES.productImage}
      src={GenericImage}
      width={isMobile ? imageSize.mobile : imageSize.desktop}
      height={isMobile ? imageSize.mobile : imageSize.desktop}
      alt={t('molecules.product_thumb.free_gift')}
    />
  )
}

/**
 * A card representing a product with a background image of the product and it's details over the top
 * @param image - the background image
 * @param alt - a description of the product image
 * @param quantity - the quantity of the product
 * @param size - the product size
 * @param {Object} props.imageSize - The size of the image on different devices.
 * @param {number} props.imageSize.mobile - The size of the image on mobile devices. Default is 112.
 * @param {number} props.imageSize.desktop - The size of the image on desktop devices. Default is 173.
 *
 * @constructor
 */
const ProductThumb = ({
  image,
  alt,
  quantity,
  imageSize = { mobile: 112, desktop: 173 },
  background = 'transparent',
  hasBorderRadius = true,
  requiresPayment = true,
  showLabel = true
}: Props): JSX.Element => {
  const productClass = classNames(STYLES.product, {
    [STYLES[background]]: background,
    [STYLES.hasBorderRadius]: hasBorderRadius
  })

  return (
    <div className={STYLES.v3}>
      <div className={productClass}>
        {showLabel && (
          <FootBar isFree={!requiresPayment} quantity={quantity || null} />
        )}

        <Image image={image} imageSize={imageSize} alt={alt} />
      </div>
    </div>
  )
}

export { Props }
export default ProductThumb
